import $ from 'jquery';
import {arrayContainsP, arrayRandomItem, arrayRandomItems} from "./arrayUtil.es6";
import {checkWrittenAnswer, parseBoolean, randomBool, scrollIntoView, shuffleArray} from "./playUtil.es6";
import Texter from "./texter.es6";
import TestChoice from "./test/TestChoice.es6";
import TestMatching from "./test/TestMatching.es6";
import TestTrueFalse from "./test/TestTrueFalse.es6";
import TestWritten from "./test/TestWritten.es6";
import CardSideAdapter from "./cardSideAdapter.es6";

require('jsrender')($);

class PackTest {
    constructor(props) {
        this.l18n = Texter.create(props.l18n);
        this.cards = props.cards || [];
        this.formStart = props.formStart;
        this.testList = props.testList;
        this.setup = null;
        this.checkers = [];

        this.formStart.find('button[data-click=startTest]').click(() => {
            if (this._validateStart()) {
                this._start();
            }
        })
    }

    _validateStart() {
        this.formStart.find('.pack-test-prepare__option-value-error').hide();

        const setup = {
            count: parseInt(this.formStart.find('input[name=questionCount]').val()),
            questionTerm: !!this.formStart.find('input[name=questionTerm]').prop('checked'),
            questionDefinition: !!this.formStart.find('input[name=questionDefinition]').prop('checked'),
            oneAnswerRequire: !!this.formStart.find('input[name=oneAnswerRequire]').prop('checked'),
            questionChoice: !!this.formStart.find('input[name=questionChoice]').prop('checked'),
            questionTrueFalse: !!this.formStart.find('input[name=questionTrueFalse]').prop('checked'),
            questionWritten: !!this.formStart.find('input[name=questionWritten]').prop('checked'),
            questionMatching: !!this.formStart.find('input[name=questionMatching]').prop('checked'),
        };

        let valid = true;
        if (!(setup.count >= 1 && setup.count <= this.cards.length)) {
            valid = false;
            this.formStart.find('div[data-bind=errorInvalidCount]').show();
        }
        if (!(setup.questionTerm || setup.questionDefinition)) {
            valid = false;
            this.formStart.find('div[data-bind=errorInvalidQuestion]').show();
        }
        if (!(setup.questionChoice || setup.questionTrueFalse || setup.questionWritten || setup.questionMatching)) {
            valid = false;
            this.formStart.find('div[data-bind=errorInvalidType]').show();
        }

        if (valid) {
            this.setup = setup;
        }
        return valid;
    }

    _start() {
        this.formStart.slideUp();

        const testCards = arrayRandomItems(this.cards, this.setup.count);

        const configuration = {
            cards: testCards,
            choice: [],
            trueFalse: [],
            written: [],
            matching: [],

            correctAnswers: 0
        };

        const candidates = [].concat(testCards);
        do {
            if (candidates.length > 0 && this.setup.questionChoice) {
                configuration.choice.push(candidates.pop());
            }
            if (candidates.length > 0 && this.setup.questionTrueFalse) {
                configuration.trueFalse.push(candidates.pop());
            }
            if (candidates.length > 0 && this.setup.questionWritten) {
                configuration.written.push(candidates.pop());
            }
            if (candidates.length > 0 && this.setup.questionMatching) {
                configuration.matching.push(candidates.pop());
            }
        } while (candidates.length > 0);

        this.test = configuration;

        this._createMatching();
        this._createChoices();
        this._createTrueFalse();
        this._createWritten();

        const setTemplate = $.templates(`
        <div class="pack-test-set">
            <div class="pack-test-set__checkButton">
                <button class="btn btn-primary" data-click="checkAnswers">${this.l18n.get('packTest.checkAnswers')}</button>
            </div>
        </div>
`);
        this.testList
            .append(setTemplate.render())
            .find('button[data-click=checkAnswers]').click(() => this.checkAnswers());
    }

    checkAnswers() {
        this.testList.find('.pack-test-set__checkButton').remove();
        this.checkers.forEach(f => f());

        const scoreTemplate = $.templates(`
<div class="pack-test-score">
    <span class="pack-test-score__title">${this.l18n.get('packTest.score.prefix')} {{> title}}</span>       
    <span class="pack-test-score__percent">{{:percent}}%</span>       
</div>
`);

        const score = {
            totals: this.test.cards.length,
            correct: this.test.correctAnswers,
        };
        score.percent = Math.floor(100 * score.correct / Math.max(score.totals, 1));

        if (score.percent >= 90) score.title = this.l18n.get('packTest.score.90');
        else if (score.percent >= 75) score.title = this.l18n.get('packTest.score.75');
        else if (score.percent >= 50) score.title = this.l18n.get('packTest.score.50');
        else if (score.percent >= 25) score.title = this.l18n.get('packTest.score.25');
        else score.title = this.l18n.get('packTest.score.0');

        this.testList.prepend(scoreTemplate.render(score));
        scrollIntoView(this.testList.find('.pack-test-score'));
    }

    _createCardTextProvider() {
        let invertCard = false;
        if (!this.setup.questionTerm) {
            invertCard = true;
        } else if (!this.setup.questionDefinition) {
            invertCard = false;
        } else {
            invertCard = randomBool();
        }

        return invertCard
            ? CardSideAdapter.backIsQuestion()
            : CardSideAdapter.faceIsQuestion();
    }

    _createMatching() {
        const tasks = this.test.matching;
        if (tasks.length === 0) {
            return;
        }

        const test = new TestMatching({
            testList: this.testList,
            sideAdapter: this._createCardTextProvider(),
            l18n: this.l18n,
            checkers: this.checkers,
            test: this.test
        });
        test.create(tasks);
    }

    _createWritten() {
        const tasks = this.test.written;
        if (tasks.length === 0) {
            return;
        }

        const test = new TestWritten({
            testList: this.testList,
            sideAdapter: this._createCardTextProvider(),
            l18n: this.l18n,
            checkers: this.checkers,
            test: this.test,
            oneAnswerRequire: this.setup.oneAnswerRequire
        });
        test.create(tasks);
    }

    _createChoices() {
        const tasks = this.test.choice;
        if (tasks.length === 0) {
            return;
        }

        const test = new TestChoice({
            testList: this.testList,
            sideAdapter: this._createCardTextProvider(),
            l18n: this.l18n,
            checkers: this.checkers,
            test: this.test,
            cards: this.cards
        });
        test.create(tasks);
    }

    _createTrueFalse() {
        const tasks = this.test.trueFalse;
        if (tasks.length === 0) {
            return;
        }

        const test = new TestTrueFalse({
            testList: this.testList,
            sideAdapter: this._createCardTextProvider(),
            l18n: this.l18n,
            checkers: this.checkers,
            test: this.test,
            cards: this.cards
        });
        test.create(tasks);
    }
}

global.PackTest = PackTest;